import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import ComparisonTable from '../../components/guides/ComparisonTable';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

export default function TrendsFacts() {
  usePageTitle('Yacht Industry Trends & Facts | Southeast Asia 2025');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1"><GuideSidebar /></div>
        <div className="lg:col-span-3">
          <h1 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Industry Trends & Facts</h1>
          <p className="text-lg text-white/70 mb-8">The global and Southeast Asian yacht markets in 2025: trends, statistics, and future outlook.</p>

          <div className="space-y-2">
            <GuideAccordion title="Southeast Asia Charter Market Growth" defaultOpen={true}>
              <ComparisonTable
                headers={['Metric', '2020', '2025 (Projected)', 'Growth']}
                rows={[
                  ['Charter Yachts Available', '850', '1,400+', '+65%'],
                  ['Annual Charter Revenue', '$180M', '$290M+', '+61%'],
                  ['Average Weekly Rate', '$35,000', '$48,000', '+37%'],
                  ['Chinese Clients (%)', '22%', '38%', '+16pt'],
                ]}
              />
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>Key Driver:</strong> Asian UHNW population grew 40% (2020-2025), seeking local luxury alternatives to Mediterranean/Caribbean.</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Popular Yacht Destinations 2025">
              <p><strong>Top 5 Southeast Asian Destinations (by charter bookings):</strong></p>
              <ol className="space-y-2 mt-4">
                <li className="flex gap-3"><span className="text-gold font-bold">1.</span><div><strong>Phuket & Andaman Sea (Thailand)</strong> - 38% market share, established infrastructure</div></li>
                <li className="flex gap-3"><span className="text-gold font-bold">2.</span><div><strong>Langkawi (Malaysia)</strong> - 18%, duty-free advantages, calm waters</div></li>
                <li className="flex gap-3"><span className="text-gold font-bold">3.</span><div><strong>Raja Ampat (Indonesia)</strong> - 15%, adventure/dive market, pristine</div></li>
                <li className="flex gap-3"><span className="text-gold font-bold">4.</span><div><strong>Palawan (Philippines)</strong> - 12%, emerging, limestone scenery</div></li>
                <li className="flex gap-3"><span className="text-gold font-bold">5.</span><div><strong>Mergui Archipelago (Myanmar)</strong> - 8%, ultra-exclusive, limited permits</div></li>
              </ol>
            </GuideAccordion>

            <GuideAccordion title="Sustainability & Green Yachting">
              <p>Environmental concerns driving innovation in Southeast Asian waters:</p>
              <ul className="space-y-2 mt-4">
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Hybrid Propulsion:</strong> 25% of new builds in 2024-2025 include hybrid/electric systems</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Solar Panels:</strong> Standard on 60% of catamarans chartered in Indonesia/Thailand</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Marine Park Fees:</strong> Rising ($50-200/day) funding coral restoration in Raja Ampat, Similan</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Plastic-Free Charters:</strong> Growing demand, 40% of clients request eco-friendly operations</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Technology Trends">
              <ul className="space-y-3">
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Starlink Internet:</strong> Revolutionizing remote cruising (Raja Ampat, Mergui) with high-speed connectivity</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Electric Tenders:</strong> Silent, eco-friendly shore access replacing diesel tenders</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Advanced Weather Routing:</strong> AI-powered apps optimizing routes for comfort/fuel efficiency</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>VR Pre-Charter Tours:</strong> Virtual reality yacht tours increasing booking confidence</div></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Interesting Yacht Facts">
              <ul className="space-y-3">
                <li className="flex gap-2"><span className="text-gold">•</span><span>World's largest superyacht: 590 ft (180m) - "Azzam", owned by UAE royal family</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Average superyacht crew: 1 crew member per 3 guests (80ft yacht = ~4 crew)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Most expensive yacht: $4.8 billion "History Supreme" (gold-plated hull!)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Catamaran market share in SE Asia: 42% (vs 18% globally)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Longest private yacht voyage: 4 years, 42,000 nautical miles around the world</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Southeast Asia vs Global Markets">
              <ComparisonTable
                headers={['Aspect', 'Southeast Asia', 'Mediterranean', 'Caribbean']}
                rows={[
                  ['Season', 'Year-round (monsoons)', 'April-October', 'November-April'],
                  ['Avg Weekly Charter', '$48,000', '$85,000', '$70,000'],
                  ['Market Maturity', 'Emerging/Growth', 'Mature/Saturated', 'Mature/Premium'],
                  ['Infrastructure', 'Growing', 'Excellent', 'Excellent'],
                  ['Uniqueness', 'Pristine/Uncrowded', 'Cultural/Historic', 'Tropical/Accessible'],
                  ['Client Base', 'Asian UHNW', 'European/US', 'North American'],
                ]}
              />
            </GuideAccordion>
          </div>

          <div className="mt-12 glass-heavy p-10 text-center">
            <h3 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Explore Southeast Asia's Waters</h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">Join the growing community of yacht enthusiasts discovering the beauty and value of Southeast Asian cruising.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/listings"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Browse Yachts</Button></Link>
              <Link to="/calculator"><Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">Calculate Charter Costs</Button></Link>
            </div>
          </div>

          <div className="mt-12 flex gap-4">
            <Link to="/guides/maintenance-safety"><Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">← Maintenance & Safety</Button></Link>
            <Link to="/guides/essentials"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Back to Start</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
